<template>
    <ui-container class="py-8 space-y-8">
        <!-- Header. -->
        <ui-header :title="`Progress Report ${item.formatted_date}`" :subtitle="`#${item.id}`">
            <template #actions>
                <ui-button-new icon="fas fa-file" extra @click="goFiles()">Files</ui-button-new>
                <template v-if="isUsedByAfp(item)">
                    <ui-button-new icon="fas fa-ban" disabled>Is used by an AFP</ui-button-new>
                </template>
                <template v-else>
                    <ui-button-new icon="fas fa-pencil" @click="edit()" :disabled="item.used_by_afp">Edit
                    </ui-button-new>
                    <ui-button-new icon="fas fa-trash" danger @click="openDeleteModal" :disabled="item.used_by_afp">
                        Delete
                    </ui-button-new>
                </template>
            </template>
        </ui-header>
        <ui-data-grid>
            <ui-data-grid-item label="Date and time" :value="item.formatted_date"></ui-data-grid-item>
            <ui-data-grid-item label="Weather" :value="item.weather"></ui-data-grid-item>
            <ui-data-grid-item label="Work as Specification" :value="item.work_as_spec"></ui-data-grid-item>
            <ui-data-grid-item label="Unfixed materials on site"
                               :value="item.unfixed_materials_value?'£'+item.unfixed_materials_value:'—'"></ui-data-grid-item>
            <ui-data-grid-item label="Quality of Workmanship" :value="item.quality_of_work"></ui-data-grid-item>
            <ui-data-grid-item wide label="House Keeping" :value="item.housekeeping"></ui-data-grid-item>
            <ui-data-grid-item wide label="Site Safety" :value="item.site_safety"></ui-data-grid-item>
            <ui-data-grid-item wide label="Comments" :value="item.comments"></ui-data-grid-item>
        </ui-data-grid>
        <div class="space-y-0">
            <div class="w-auto py-2 my-2 border-t border-b border-gray-200">
                <ui-toggle class="mb-0" :field="showCompleted?'text-green-700':'text-red-500'" label="Show Completed"
                           :text="totalHidden+'/'+totalItems+(!showCompleted?' Items are completed and hidden - toggle to unhide':' Items are completed - toggle to hide')"
                           name="show_completed" v-model="showCompleted"></ui-toggle>
            </div>
            <template v-for="quote in controlledQuotes">
                <template
                    v-if="(showCompleted || quote.preliminaries.some((x) => (calculatePreviousForPrelim(x) !== '100%')) || quote.tasks.some((y) => y.items.some((x) => (calculatePreviousForItem(x) !== '100%') && (!x.is_omitted))))">
                    <label class="py-3 mt-4" :class="getItemClass(quote,item)" v-if="quote.is_variation">Variation
                        {{ quote.variation_number }} -
                        ({{ quote.reference }}) <span
                            class="text-xs text-gray-300"> - Secured at {{ quote.secured_at.toLocaleString() }} </span>
                    </label>
                    <label class="py-3 mt-4" :class="getItemClass(quote,item)" v-else>Original Contract -
                        ({{ quote.reference }})<span
                            class="text-xs text-gray-300"> - Secured at {{ quote.secured_at.toLocaleString() }} </span>
                    </label>
                    <div class="grid grid-cols-12 my-2 border-gray-200 text-xs text-gray-500 font-semibold">
                        <div class="col-span-1 border-t border-l px-2 py-2" :class="getItemClass(quote)">Item</div>
                        <div class="col-span-8 border-t px-1 py-2" :class="getItemClass(quote)">Description</div>
                        <div class="col-span-1 border-t px-1 py-2" :class="getItemClass(quote)">Qty</div>
                        <div class="col-span-1 border-t px-1 py-2 text-center" :class="getItemClass(quote)">Previous
                        </div>
                        <div class="col-span-1 border-t border-r px-1 py-2 text-center" :class="getItemClass(quote)">
                            Submitted
                        </div>
                        <template v-for="task in quote.tasks">
                            <template
                                v-if="(showCompleted || task.items.some((x) => (calculatePreviousForItem(x) !== '100%') && (!x.is_omitted)))">
                                <div class="col-span-1 border-t border-l bg-gray-50 px-2 py-2"
                                     :class="getItemClass(quote,task)">{{ task.reference }}
                                </div>
                                <div class="col-span-11 border-t border-r bg-gray-50 px-1 py-2"
                                     :class="getItemClass(quote,task)">{{ task.description }}
                                </div>
                                <template v-for="item in task.items">
                                    <template v-if="shouldItemBeVisible(item)">
                                        <div class="col-span-1 border-t border-l px-4 py-2"
                                             :class="getItemClass(quote,item)">
                                            {{ item.reference }}
                                        </div>
                                        <div class="col-span-8 border-t px-1 py-2" :class="getItemClass(quote,item)">
                                            {{ item.description }}
                                        </div>
                                        <div class="col-span-1 border-t px-1 py-2" :class="getItemClass(quote,item)">{{
                                                item.qty
                                            }}
                                            {{ item.unit }}
                                        </div>
                                        <ui-dropdown placement="bottom-start" menu-class="p-3 w-56 flex"
                                                     class="flex col-span-1"
                                                     trigger-class="flex w-full">
                                            <template #default="{ open, close }">
                                                <div
                                                    @mouseover="() => {store.state.lastCompleted['Quote'+quote.id+'|Task'+task.id+'|Item'+item.id]?open():null}"
                                                    @mouseleave="() => {store.state.lastCompleted['Quote'+quote.id+'|Task'+task.id+'|Item'+item.id]?close():null}"
                                                    @click="() => {store.state.lastCompleted['Quote'+quote.id+'|Task'+task.id+'|Item'+item.id]?goPr(store.state.lastCompleted['Quote'+quote.id+'|Task'+task.id+'|Item'+item.id]):null}"
                                                    class="border-t px-1 py-2 text-center w-full"
                                                    :class="calculateClass(calculatePreviousForItem(item))">
                                                    {{ calculatePreviousForItem(item) }}
                                                </div>
                                            </template>
                                            <template #menu>
                                                <table class="w-full"
                                                       v-if="store.state.lastCompleted['Quote' + quote.id + '|Task' + task.id + '|Item' + item.id]">
                                                    <tbody>
                                                    <tr>
                                                        <td class="text-gray-600 text-xs">Last marked as completed
                                                            (100%) :
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td class="text-gray-600 text-xs">
                                                            {{
                                                                store.state.lastCompleted['Quote' + quote.id + '|Task' + task.id + '|Item' + item.id].updated_at +
                                                                ' (' + store.state.lastCompleted['Quote' + quote.id + '|Task' + task.id + '|Item' + item.id].updated_by + ')'
                                                            }}
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </template>
                                        </ui-dropdown>
                                        <div class="col-span-1 border-t border-r px-1 py-2 text-center"
                                             :class="calculateClass(submissionForItem(item))">{{
                                                submissionForItem(item)
                                            }}
                                        </div>
                                    </template>
                                </template>
                            </template>
                        </template>
                        <div class="col-span-12 border-t border-r px-0 py-0"></div>
                        <template
                            v-if="quote.preliminaries.length && (showCompleted || quote.preliminaries.some((x) => (calculatePreviousForPrelim(x) !== '100%')))">
                            <div class="col-span-12 py-2 px-1 my-1 text-sm" :class="getItemClass(quote)">Preliminaries
                            </div>
                            <template v-for="preliminary in quote.preliminaries">
                                <template
                                    v-if="(showCompleted || calculatePreviousForPrelim(preliminary) !== '100%')">
                                    <div class="col-span-1 border-t border-l px-1 py-2"
                                         :class="getItemClass(quote,preliminary)">{{ preliminary.reference }}
                                    </div>
                                    <div class="col-span-8 border-t px-1 py-2" :class="getItemClass(quote,preliminary)">
                                        {{ preliminary.description }}
                                    </div>
                                    <div class="col-span-1 border-t px-1 py-2" :class="getItemClass(quote,preliminary)">
                                        {{ preliminary.qty }} {{ preliminary.unit }}
                                    </div>
                                    <ui-dropdown placement="bottom-start" menu-class="p-3 w-56 flex"
                                                 class="flex col-span-1"
                                                 trigger-class="flex w-full">
                                        <template #default="{ open, close }">
                                            <div
                                                @mouseover="() => {store.state.lastCompleted['Quote'+quote.id+'|Prelim'+preliminary.id]?open():null}"
                                                @mouseleave="() => {store.state.lastCompleted['Quote'+quote.id+'|Prelim'+preliminary.id]?close():null}"
                                                @click="() => {store.state.lastCompleted['Quote'+quote.id+'|Prelim'+preliminary.id]?goPr(store.state.lastCompleted['Quote'+quote.id+'|Prelim'+preliminary.id]):null}"
                                                class="border-t px-1 py-2 text-center w-full"
                                                :class="calculateClass(calculatePreviousForPrelim(preliminary))">
                                                {{ calculatePreviousForPrelim(preliminary) }}
                                            </div>
                                        </template>
                                        <template #menu>
                                            <table class="w-full"
                                                   v-if="store.state.lastCompleted['Quote' + quote.id + '|Prelim' + preliminary.id]">
                                                <tbody>
                                                <tr>
                                                    <td class="text-gray-600 text-xs">Last marked as completed
                                                        (100%) :
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td class="text-gray-600 text-xs">
                                                        {{
                                                            store.state.lastCompleted['Quote' + quote.id + '|Prelim' + preliminary.id].updated_at +
                                                            ' (' + store.state.lastCompleted['Quote' + quote.id + '|Prelim' + preliminary.id].updated_by + ')'
                                                        }}
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </template>
                                    </ui-dropdown>

                                    <div class="col-span-1 border-t border-r px-1 py-2 text-center"
                                         :class="calculateClass(submissionForPrelim(preliminary))">
                                        {{ submissionForPrelim(preliminary) }}
                                    </div>
                                </template>
                            </template>
                            <div class="col-span-12 border-t border-r border-l px-0 py-0"></div>
                        </template>
                    </div>
                </template>
            </template>
            <div
                v-if="!showCompleted && (totalHidden === totalItems)"
                class="mt-3 mb-3">
                <div class="rounded-xl my-4 bg-gray-50 border-gray-200 border p-4 text-green-600 italic text-center">
                    All Contracts and Variations have been completed
                </div>
            </div>

        </div>
        <!--
        <ui-data-grid-item label="Quick action" wide>
            <div class="flex flex-col md:flex-row flex-wrap -m-2">
                <ui-button-new class="m-2" icon="fas fa-print">Print PDF</ui-button-new>
            </div>
        </ui-data-grid-item>
        -->
        <ui-data-grid>
            <ui-data-grid-item label="Created By">{{ item.created_by }} on {{ item.created_at }}</ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By">{{ item.updated_by }} on {{ item.updated_at }}
            </ui-data-grid-item>
        </ui-data-grid>
    </ui-container>
</template>

<script>
import AutoresizableTextarea from "../ui/AutoresizableTextarea";
import UiContainer from "../ui/UiContainer.vue";
import UiDropdownItem from "../ui/UiDropdownItem.vue";
import UiDropdown from "../ui/UiDropdown.vue";
import UiButtonNew from "../ui/UiButtonNew.vue";
import UiDataGrid from "../ui/UiDataGrid.vue";
import UiDataGridItem from "../ui/UiDataGridItem.vue";
import {progressReportStore} from "../../stores";
import UiHeader from "../ui/UiHeader.vue";
import moment from "moment";
import UiInput from "../ui/UiInput.vue";
import UiToggle from "../ui/UiToggle.vue";
import TEMPLATE from "../TEMPLATE.vue";
import {cloneDeep} from "lodash";

export default {
    components: {
        TEMPLATE,
        UiToggle,
        UiInput,
        UiHeader,
        UiDataGridItem,
        UiDataGrid,
        UiButtonNew, UiDropdown, UiDropdownItem, UiContainer, AutoresizableTextarea
    },
    props: {
        item: Object,
        edit: Function,
        store: progressReportStore,
        selectItem: Function,
        createItem: Function,
        removeItem: Function,
    },
    data() {
        return {
            quotes: this.store.state.quotes,
            showCompleted: true,
            totalItems: 0,
            totalHidden: 0,
        }
    },
    computed: {
        controlledQuotes() {
            return this.store.state.quotes;
        }
    },
    methods: {
        goFiles() {
            const date = moment(this.item.date);
            const link = route('mobile.estimation.packages.filesystem.path', {
                'packageReference': this.item.package.reference,
                'path': 'Progress Reports/' + date.format('YYYY-MM-DD') + ' - Progress Report'
            });
            window.location.replace(link);
        },
        openDeleteModal() {
            const title = 'Are you sure you want to remove progress report ' + this.item.formatted_date + '?'
            const description = 'You are removing progress report #' + this.item.id + '. Are you sure you want to do this?'

            eventBus.$emit('delete-modal-open', title, description, async () => {
                await this.store.deleteItem(this.item)
                this.removeItem(this.item)

            });
        },
        calculatePreviousForItem(item) {
            let previous_item = this.store.state.items.filter(el => el.date < this.item.date).find(el => el.submitted_item_values.find(el2 => el2.item_id === item.id));
            if (previous_item) {
                return previous_item.submitted_item_values.find(el => el.item_id === item.id).percent + '%'
            }
            return ''
        },
        calculatePreviousForPrelim(prelim) {
            let previous_item = this.store.state.items.filter(el => el.date < this.item.date).find(el => el.submitted_preliminary_values.find(el2 => el2.preliminary_id === prelim.id));
            if (previous_item) {
                return previous_item.submitted_preliminary_values.find(el => el.preliminary_id === prelim.id).percent + '%'

            }
            return '';
        },
        submissionForItem(item) {
            let submitted = this.item.submitted_item_values.find(el => el.item_id === item.id);
            if (submitted) {
                return submitted.percent + '%';
            }
            return ''
        },
        submissionForPrelim(prelim) {
            let submitted = this.item.submitted_preliminary_values.find(el => el.preliminary_id === prelim.id);
            if (submitted) {
                return submitted.percent + '%';
            }
            return ''
        },
        getItemClass(quote, item = {}) {
            let ret = (this.checkDate(quote.secured_at)) ? 'text-gray-300' : '';
            if (item.is_omitted) {
                ret = 'text-red-500 line-through';
            }
            return ret;
        },
        checkDate(date) {
            return this.item.date < date;
        },
        calculateClass(value) {
            if (value === '') return '';
            if (value === '100%') return 'bg-green-100';
            return 'bg-yellow-100';
        },
        shouldItemBeVisible(item) {
            return this.showCompleted || ((this.calculatePreviousForItem(item) !== '100%') && (!item.is_omitted))
        },
        isUsedByAfp(item) {
            return !this.store.state.items.filter((i) => i.date >= item.date).every((i) => !i.used_by_afp);
        },
        goPr(item) {
            this.selectItem(item);
        }
    },
    mounted() {
        let hidden = 0
        let total = 0;
        this.controlledQuotes.forEach((q) => {
            q.preliminaries.forEach((p) => {
                total++;
                if (this.calculatePreviousForPrelim(p) === '100%') {
                    hidden++;
                }
            })
            q.tasks.forEach((t) => {
                t.items.forEach((i) => {
                    if (!i.is_omitted) {
                        total++;
                        if (this.calculatePreviousForItem(i) === '100%') {
                            hidden++;
                        }
                    }
                })
            })
        })
        this.totalHidden = hidden
        this.totalItems = total
    }
}
</script>
